<template>
  <div id="notifications">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="notification-container mb-16">
          <div class="route-container mb-4 section-heading">
            <h1>
              <AppGuideIcon :color="iconColor" class="terms-icon" />
              {{ $t('notifications.pageTitle') }}
            </h1>
          </div>
          <div
            class="editor-container"
            :style="{ backgroundColor: editorBackgroundColor }"
          >
            <GlobalQuillEditor
              :initialData="{ content: notification }"
              :allowed-roles="['admin', 'superAdmin']"
              :autosave="false"
              @update-content="(val) => (notification = val)"
            />

            <v-row align="center" align-content="center" class="mx-0 mb-5">
              <span class="font-weight-bold">{{
                $t('notifications.audienceLabel')
              }}</span>
              <v-checkbox
                v-model="audience"
                value="member"
                :color="checkboxColor"
                hide-details
                class="mt-0 mr-5 ml-3"
              >
                <template #label>
                  <div>
                    <v-tooltip location="bottom">
                      <template #activator="{ props }">
                        <span v-bind="props">{{
                          $t('notifications.audienceTypes.allUsers')
                        }}</span>
                      </template>
                      {{ $t('notifications.audienceTooltips.allUsers') }}
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
              <v-checkbox
                v-model="audience"
                value="leader"
                :color="checkboxColor"
                hide-details
                class="mt-0 mr-5"
              >
                <template #label>
                  <div>
                    <v-tooltip location="bottom">
                      <template #activator="{ props }">
                        <span v-bind="props">{{
                          $t('notifications.audienceTypes.groupLeaders')
                        }}</span>
                      </template>
                      {{ $t('notifications.audienceTooltips.groupLeaders') }}
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
              <v-checkbox
                v-if="checkAuthorization(['superAdmin'])"
                v-model="audience"
                value="admin"
                :color="checkboxColor"
                hide-details
                class="mt-0 mr-5"
              >
                <template #label>
                  <div>
                    <v-tooltip location="bottom">
                      <template #activator="{ props }">
                        <span v-bind="props">{{
                          $t('notifications.audienceTypes.churchAdmins')
                        }}</span>
                      </template>
                      {{ $t('notifications.audienceTooltips.churchAdmins') }}
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
              <v-checkbox
                v-if="checkAuthorization(['superAdmin']) && selectedAudience"
                v-model="audience"
                value="allchurches"
                :color="checkboxColor"
                hide-details
                class="mt-0 mr-5"
              >
                <template #label>
                  <div>
                    <v-tooltip location="bottom">
                      <template #activator="{ props }">
                        <span v-bind="props">{{
                          $t('notifications.audienceTypes.allChurches')
                        }}</span>
                      </template>
                      {{ $t('notifications.audienceTooltips.allChurches') }}
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
              <v-checkbox
                v-if="checkAuthorization(['superAdmin'])"
                v-model="audience"
                value="permanent"
                :color="checkboxColor"
                hide-details
                class="mt-0 mr-5"
              >
                <template #label>
                  <div>
                    <v-tooltip location="bottom">
                      <template #activator="{ props }">
                        <span v-bind="props">{{
                          $t('notifications.audienceTypes.permanent')
                        }}</span>
                      </template>
                      {{ $t('notifications.audienceTooltips.permanent') }}
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
            </v-row>
            <v-row align="center" align-content="center" class="mx-0 mb-5">
              <v-col class="d-flex pa-0" cols="12" sm="3">
                <v-select
                  v-model="selectedType"
                  :items="types"
                  item-title="text"
                  item-value="value"
                  :label="$t('notifications.typeLabel')"
                  density="compact"
                  variant="underlined"
                  color="#A5D867"
                  class="notification-type-select"
                  hide-details
                />
              </v-col>
            </v-row>
            <div class="mb-0 mt-0">
              <v-btn
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold send-btn"
                @click="sendNotification"
                :disabled="audience.length == 0 || selectedType == null"
              >
                {{ $t('notifications.sendButton') }}</v-btn
              >
              <v-progress-circular
                v-if="sending"
                :active="sending"
                :indeterminate="sending"
                color="rgba(165, 216, 103, 1)"
                class="ml-4"
              ></v-progress-circular>
            </div>
          </div>
        </div>
        <div class="d-flex pa-2 justify-end">
          <v-menu location="top left" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-view-column</v-icon>
            </template>
            <v-list class="header-menu-list">
              <v-list-item v-for="(h, i) in headers" :key="i">
                <v-checkbox
                  v-model="selectedHeaders"
                  :value="h"
                  :label="h.title"
                  density="compact"
                  color="#A5D867"
                  hide-details
                  multiple
                  @update:model-value="setShowHeaders"
                >
                </v-checkbox>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <!-- took this out from data table below item-key="created" -->
        <v-data-table
          :headers="showHeaders"
          fixed-header
          :loading="isloading"
          :loading-text="$t('notifications.loading')"
          :no-data-text="$t('notifications.noData')"
          v-model:expanded="expanded"
          item-key="id"
          single-expand
          :items="displayNotifications"
          :items-per-page="20"
          :header-props="{ sortIcon: '$sortarrowdown' }"
          :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50, -1] }"
          class="elevation-1 notifications-table"
        >
          <template #progress>
            <v-progress-linear
              color="#592892"
              indeterminate
            ></v-progress-linear>
          </template>
          <template #[`item.created`]="{ item }">
            <div v-if="item.created">
              {{ formatDate(item.created) }}
            </div>
          </template>
          <template #[`item.notification`]="{ item }">
            <div v-if="getNotificationContent(item)" class="">
              <span
                class="notification-wrapper"
                v-html="getNotificationContent(item)"
              ></span>
            </div>
          </template>
          <template #expanded-row="{ item, columns }">
            <tr>
              <td :colspan="columns.length">
                <div class="snippet-autosave-header">
                  <div
                    class="snippet-autosave-status"
                    :class="'snippet-autosave-status-' + item.id"
                  >
                    <div class="snippet-autosave-status_label">
                      Status:
                      {{
                        isNotificationReadOnly(item)
                          ? 'Read-only (Translated content)'
                          : 'Ready!'
                      }}
                    </div>
                    <div
                      v-if="!isNotificationReadOnly(item)"
                      class="snippet-autosave-status_spinner"
                    >
                      <span
                        class="snippet-autosave-status_spinner-label"
                      ></span>
                      <span
                        class="snippet-autosave-status_spinner-loader"
                      ></span>
                    </div>
                  </div>
                  <div
                    v-if="isNotificationReadOnly(item)"
                    class="read-only-warning"
                  >
                    <v-alert
                      type="warning"
                      variant="tonal"
                      density="compact"
                      class="mt-2"
                    >
                      This is translated content. Edit the
                      {{ item.sourceLocale || 'en' }} version to make changes.
                    </v-alert>
                  </div>
                </div>
                <CustomQuill
                  v-model:content="item.notification"
                  :modules="customModulesForEditor"
                  :placeholder="
                    isNotificationReadOnly(item)
                      ? 'Read-only content'
                      : 'Enter Message Here'
                  "
                  :readonly="isNotificationReadOnly(item)"
                  @update:content="onTextChange(item)"
                />
              </td>
            </tr>
          </template>
          <template #[`item.messagetype`]="{ item }">
            <div v-if="showedit === item.id">
              <v-select
                v-model="item.messagetype"
                :items="types"
                item-title="text"
                item-value="value"
                density="compact"
                variant="underlined"
                hide-details
                color="#A5D867"
                class="type-select"
                @update:model-value="editNotification(item)"
              ></v-select>
            </div>
            <div v-else>
              <span>{{ capitalize(item.messagetype) }}</span>
            </div>
          </template>
          <template
            v-if="
              isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
            "
            #[`item.active`]="{ item }"
          >
            <div>
              <v-checkbox
                color="#A5D867"
                class="ma-0 pt-0 activate-switch"
                true-icon="mdi-radiobox-marked"
                false-icon="mdi-radiobox-blank"
                v-model="item.active"
                @update:model-value="activateNotification(item)"
                hide-details
              >
              </v-checkbox>
            </div>
          </template>
          <template #[`item.audience`]="{ item }">
            <div v-if="showedit === item.id">
              <v-select
                :items="audienceTypes"
                item-title="text"
                item-value="value"
                multiple
                :return-object="false"
                :model-value="item.audience"
                class="audience-select"
                @update:model-value="
                  (val) => {
                    item.audience = normalizeAudience(val)
                    editNotification(item)
                  }
                "
              />
            </div>
            <div v-else>
              <span v-for="(a, index) in item.audience" :key="index">
                <span v-if="index != 0">, </span
                ><span>{{ capitalize(a) }}</span>
              </span>
            </div>
          </template>
          <template
            v-if="
              isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
            "
            #[`item.actions`]="slotData"
          >
            <div class="action-icons">
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <v-icon
                    v-bind="props"
                    @click="expand(slotData)"
                    class="edit-icon mr-3"
                    :class="{
                      'disabled-icon': isNotificationReadOnly(slotData.item),
                    }"
                    :style="{
                      opacity: isNotificationReadOnly(slotData.item) ? 0.5 : 1,
                      cursor: isNotificationReadOnly(slotData.item)
                        ? 'not-allowed'
                        : 'pointer',
                    }"
                    >edit</v-icon
                  >
                </template>
                <span v-if="isNotificationReadOnly(slotData.item)">
                  Read-only ({{ slotData.item.sourceLocale || 'en' }})
                </span>
                <span v-else> Edit ({{ currentLocale }}) </span>
              </v-tooltip>
              <v-icon
                v-if="!isHelpDesk()"
                @click.native="deleteNotification(slotData)"
                >delete_icon</v-icon
              >
            </div>
          </template>
        </v-data-table>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { mapGetters } from 'vuex'
  import GlobalQuillEditor from '@/components/utilities/GlobalQuillEditor.vue'
  import CustomQuill from '@/components/utilities/CustomQuill.vue'
  import { fullEditorModules as customModulesForEditor } from '@/QuillConfig'
  import { currentTheme } from '@/plugins/vuetify'
  import AppGuideIcon from '@/assets/icons/AppGuideIcon.vue'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import { formatDateWithTime } from '@/components/utilities/formatters'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'Notifications',
    components: {
      HowToUseThisPage,
      GlobalQuillEditor,
      CustomQuill,
      AppGuideIcon,
    },
    data() {
      return {
        feedback: null,
        message: '',
        notification: '',
        notifications: [],
        audience: [],
        isloading: true,
        sending: false,
        expanded: [],
        showedit: false,
        timeout: null,
        selectedHeaders: [],
        columnDialog: false,
        customModulesForEditor,
        selectedType: null,
        dateFormatConfig: {
          dayOfWeekNames: [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ],
          dayOfWeekNamesShort: ['Su', 'Mo', 'Tu', 'We', 'Tr', 'Fr', 'Sa'],
          monthNames: [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
          ],
          monthNamesShort: [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ],
          // Timezone offset, in minutes (0 - UTC, 180 - Russia, undefined - current)
          timezone: 0,
        },
      }
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      editorBackgroundColor() {
        return currentTheme.value === 'dark' ? '#272727' : '#ffffff'
      },
      checkboxColor() {
        return currentTheme.value === 'dark' ? '#8a40e0' : 'primary'
      },
      types() {
        return [
          { text: this.$t('notifications.types.notify'), value: 'notify' },
          { text: this.$t('notifications.types.warning'), value: 'warning' },
          { text: this.$t('notifications.types.critical'), value: 'critical' },
        ]
      },
      headers() {
        return [
          {
            title: this.$t('notifications.headers.cid'),
            key: 'cid',
            sortable: true,
            width: '85px',
          },
          {
            title: this.$t('notifications.headers.sentOn'),
            key: 'created',
            sortable: true,
            width: '190px',
          },
          {
            title: this.$t('notifications.headers.submittedBy'),
            key: 'submittedby',
            sortable: false,
          },
          {
            title: this.$t('notifications.headers.message'),
            key: 'notification',
            sortable: false,
          },
          {
            title: this.$t('notifications.headers.type'),
            key: 'messagetype',
            sortable: true,
            filterable: false,
            width: '105px',
          },
          {
            title: this.$t('notifications.headers.active'),
            key: 'active',
            sortable: true,
            filterable: false,
            width: '105px',
          },
          {
            title: this.$t('notifications.headers.audience'),
            key: 'audience',
            sortable: false,
            filterable: false,
            width: '120px',
          },
          {
            title: this.$t('notifications.headers.actions'),
            key: 'actions',
            sortable: false,
            align: 'end',
          },
        ]
      },
      showHeaders() {
        return this.headers.filter((s) =>
          this.selectedHeaders.some((selected) => selected.key === s.key)
        )
      },
      audienceTypes() {
        const types = [
          {
            text: this.$t('notifications.audienceTypes.allUsers'),
            value: 'member',
          },
          {
            text: this.$t('notifications.audienceTypes.groupLeaders'),
            value: 'leader',
          },
        ]

        if (this.isAuthenticated && this.checkAuthorization(['superAdmin'])) {
          types.push(
            {
              text: this.$t('notifications.audienceTypes.churchAdmins'),
              value: 'admin',
            },
            {
              text: this.$t('notifications.audienceTypes.allChurches'),
              value: 'allchurches',
            },
            {
              text: this.$t('notifications.audienceTypes.permanent'),
              value: 'permanent',
            }
          )
        }

        return types
      },
      displayNotifications() {
        return this.notifications.filter((item) => {
          if (this.checkAuthorization(['superAdmin'])) {
            return item
          } else {
            if (!item.audience.includes('allchurches')) return item
          }
        })
      },
      selectedAudience() {
        const t = ['member', 'leader', 'admin']
        return t.some((i) => this.audience.includes(i))
      },
      currentLocale() {
        return this.$route.params.locale || 'en'
      },
    },
    watch: {
      churchid: {
        deep: true,
        handler() {
          this.getNotifications()
        },
      },
      '$i18n.locale': {
        handler(newLocale) {
          // Set cached locale immediately
          this._cachedCurrentLocale = newLocale || 'en'

          // Debounce language changes to prevent ResizeObserver errors
          if (this.timeout) {
            clearTimeout(this.timeout)
          }
          this.timeout = setTimeout(() => {
            // Allow DOM to settle after language change
            this.$nextTick(() => {
              // Additional delay to prevent ResizeObserver loop
              setTimeout(() => {
                // DOM should be stable now
              }, 200)
            })
          }, 300)
        },
        immediate: false,
      },
    },
    methods: {
      // Check if a notification should be read-only based on source locale
      isNotificationReadOnly(item) {
        const currentLocale = this.currentLocale
        const sourceLocale = item.sourceLocale || 'en'
        return currentLocale !== sourceLocale
      },
      // Get the current locale for display purposes
      getCurrentLocale() {
        return this.$route.params.locale || 'en'
      },
      toggle() {
        this.autoselectMenu = !this.autoselectMenu
      },
      setShowHeaders() {
        localStorage.setItem(
          'setNotificationsHeaders',
          JSON.stringify(this.selectedHeaders)
        )
      },
      formatDate(timestamp) {
        return formatDateWithTime(timestamp)
      },
      capitalize(value) {
        if (!value || typeof value !== 'string') return ''
        const map = {
          member: this.$t('notifications.audienceTypes.allUsers'),
          leader: this.$t('notifications.audienceTypes.groupLeaders'),
          admin: this.$t('notifications.audienceTypes.churchAdmins'),
          allchurches: this.$t('notifications.audienceTypes.allChurches'),
          permanent: this.$t('notifications.audienceTypes.permanent'),
        }
        return map[value] || value.charAt(0).toUpperCase() + value.slice(1)
      },
      activateNotification(item) {
        // console.log(item)
        if (item.audience.includes('allchurches')) {
          let notificationRef = db
            .collection('notifications')
            .where('created', '==', item.created)
            .where('audience', 'array-contains-any', ['allchurches'])
          notificationRef
            .get()
            .then((snap) => {
              var batch = db.batch()
              snap.forEach((doc) => {
                batch.update(doc.ref, {
                  active: item.active,
                })
              })
              batch
                .commit()
                .then(() => {
                  this.getNotifications()
                })
                .catch((err) => {
                  console.error('Error updating notifications:', err)
                  this.feedback =
                    'Error updating notifications. Please try again.'
                })
            })
            .catch((err) => {
              console.error('Error fetching notifications:', err)
              this.feedback = 'Error fetching notifications. Please try again.'
            })
        } else {
          db.collection('notifications')
            .doc(item.id)
            .update({
              active: item.active,
            })
            .catch((err) => {
              console.log(err)
            })
        }
      },
      expand(slotData) {
        // Don't expand if notification is read-only
        if (this.isNotificationReadOnly(slotData.item)) {
          console.log('Cannot expand read-only notification')
          return
        }

        const itemId = slotData.item.id
        const index = this.expanded.findIndex((id) => id === itemId)

        if (index > -1) {
          this.expanded.splice(index, 1)
        } else {
          this.expanded = [itemId] // respect single-expand
        }

        this.showedit = this.showedit === itemId ? null : itemId
      },
      async sendNotification() {
        if (!this.churchid && !this.audience.includes('allchurches')) {
          Swal.fire({
            title: this.$t('notifications.errors.uhOhHouston'),
            text: this.$t('notifications.errors.noCid'),
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return false
        }

        if (!this.notification) {
          Swal.fire({
            title: this.$t('notifications.errors.houston'),
            text: this.$t('notifications.errors.noMessage'),
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return false
        }

        const confirmed = await confirmAction({
          title: this.$t('notifications.dialogs.send.title'),
          html: this.$t('notifications.dialogs.send.instruction'),
          input: 'text',
          inputPlaceholder: this.$t('notifications.dialogs.send.placeholder'),
          inputValidator: (value) => {
            return new Promise((resolve) => {
              const expectedWord = this.$t(
                'notifications.dialogs.send.verification'
              )

              console.log('Expected word:', JSON.stringify(expectedWord))
              console.log('User input:', JSON.stringify(value))
              console.log('Match:', value === expectedWord)

              if (matchesConfirmWord(value, 'SEND', expectedWord)) {
                resolve()
              } else {
                resolve(this.$t('notifications.dialogs.send.errorText'))
              }
            })
          },
          confirmButtonText: this.$t('notifications.dialogs.send.confirmText'),
          cancelButtonText: this.$t('notifications.dialogs.send.cancelText'),
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        this.sending = true
        const getUser = this.$store.getters['UserData/getUser']
        const timestamp = Date.now()

        if (this.audience.includes('allchurches')) {
          const batch = db.batch()
          const churchesRef = db
            .collection('churches')
            .where('active', '==', true)

          const snapshot = await churchesRef.get()
          snapshot.forEach((doc) => {
            const notificationRef = db.collection('notifications').doc()
            batch.set(notificationRef, {
              active: true,
              cid: doc.data().cid,
              notification: this.notification,
              audience: this.audience,
              messagetype: this.selectedType.toLowerCase(),
              submittedby: getUser.email,
              created: timestamp,
              autoTranslate: true,
              sourceLocale: this.$route.params.locale || 'en',
            })
          })

          await batch.commit()
          this.getNotifications()
        } else {
          setTimeout(async () => {
            try {
              await db.collection('notifications').add({
                active: true,
                cid: this.churchid,
                notification: this.notification,
                audience: this.audience,
                messagetype: this.selectedType.toLowerCase(),
                submittedby: getUser.email,
                created: timestamp,
                autoTranslate: true,
                sourceLocale: this.$route.params.locale || 'en',
              })
              this.getNotifications()
            } catch (err) {
              console.error(err)
            }
          }, 1000)
        }
      },
      onTextChange(item) {
        // Don't save if this notification is read-only
        if (this.isNotificationReadOnly(item)) {
          console.log('Skipping save - notification is read-only')
          return
        }
        this.editNotification(item)
      },
      editNotification(item) {
        // Normalize audience first
        item.audience = this.normalizeAudience(item.audience)

        // const statusIndicator = document.querySelectorAll('.notifications-table .snippet-autosave-status')
        const statusIndicator = document.querySelectorAll(
          '.snippet-autosave-status-' + item.id
        )

        statusIndicator.forEach(function (item) {
          item.classList.add('busy')
        })

        // Lets debouce this
        if (this.timeout) clearTimeout(this.timeout)

        this.timeout = setTimeout(() => {
          if (item.audience.includes('allchurches')) {
            let notificationRef = db
              .collection('notifications')
              .where('created', '==', item.created)
              .where('audience', 'array-contains-any', ['allchurches'])
            notificationRef.get().then((snap) => {
              var batch = db.batch()
              snap.forEach((doc) => {
                batch.update(doc.ref, {
                  notification: item.notification,
                  audience: item.audience,
                  messagetype: item.messagetype,
                })
              })
              batch
                .commit()
                .then(() => {
                  statusIndicator.forEach(function (item) {
                    item.classList.remove('busy')
                  })
                })
                .catch((err) => {
                  console.error('Error updating notifications:', err)
                  this.feedback =
                    'Error updating notifications. Please try again.'
                })
            })
          } else {
            // your action
            db.collection('notifications')
              .doc(item.id)
              .update({
                notification: item.notification,
                audience: item.audience,
                messagetype: item.messagetype,
              })
              .then(() => {
                statusIndicator.forEach(function (item) {
                  item.classList.remove('busy')
                })
              })
              .catch((err) => {
                console.log(err)
              })
          }
        }, 500)
      },
      async deleteNotification(data) {
        if (this.isHelpDesk && this.isHelpDesk()) {
          const key = 'notifications.messages.deleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete notifications.'
          return
        }

        const isDark = this.$isDarkTheme()
        if (!data.item) {
          Swal.fire({
            title: this.$t('notifications.errors.houston'),
            text: this.$t('notifications.errors.deleteError'),
            icon: 'error',
            isDark,
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return
        }

        const confirmed = await confirmAction({
          title: this.$t('notifications.dialogs.delete.title'),
          html: this.$t('notifications.dialogs.delete.instruction'),
          input: 'text',
          isDark,
          inputPlaceholder: this.$t('notifications.dialogs.delete.placeholder'),
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (matchesConfirmWord(value, 'DELETE')) {
                resolve()
              } else {
                resolve(this.$t('notifications.dialogs.delete.errorText'))
              }
            })
          },
          confirmButtonText: this.$t(
            'notifications.dialogs.delete.confirmText'
          ),
          cancelButtonText: this.$t('notifications.dialogs.delete.cancelText'),
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        if (data.item.audience.includes('allchurches')) {
          const notificationRef = db
            .collection('notifications')
            .where('created', '==', data.item.created)
            .where('audience', 'array-contains-any', ['allchurches'])

          const snap = await notificationRef.get()
          const batch = db.batch()

          snap.forEach((doc) => {
            batch.delete(doc.ref)
          })

          await batch.commit()
          this.getNotifications()
        } else {
          await db.collection('notifications').doc(data.item.id).delete()
          this.getNotifications()
        }
      },
      normalizeAudience(aud) {
        return Array.isArray(aud)
          ? aud.map((x) => (typeof x === 'string' ? x : x?.value || ''))
          : []
      },
      getNotifications() {
        // console.log('getNotifications ', this.churchid)
        this.notification = ''
        this.audience = []
        this.selectedType = null
        this.isloading = true
        this.sending = false
        this.notifications = []

        let notificationsRef = db.collection('notifications')
        if (this.churchid !== null)
          notificationsRef = notificationsRef.where('cid', '==', this.churchid)
        notificationsRef
          .get()
          .then((snapshot) => {
            snapshot.forEach((doc) => {
              let msgs = doc.data()
              msgs.id = doc.id
              msgs.audience = this.normalizeAudience(msgs.audience)
              this.notifications.push(msgs)
            })
            // console.log(this.message)
          })
          .then(() => {
            this.notifications.sort((a, b) => b.created - a.created)

            this.isloading = false
          })
      },
      imageURL(file) {
        console.log(file)
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const normalizedPermissions = permissions.includes('superAdmin')
            ? [...permissions, 'helpDesk']
            : permissions

          const hasRole = normalizedPermissions.filter(() =>
            userRoles.some((role) => normalizedPermissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      getNotificationContent(item) {
        // Get the current locale (from URL param)
        const currentLang = this.$route.params.locale || 'en'

        // Check if this notification has variants (translated content)
        if (item.variants && item.variants.default) {
          const variants = item.variants.default

          // Try to get content for current language
          if (variants[currentLang]) {
            return variants[currentLang]
          }

          // Fallback to English if current language not available
          if (variants.en) {
            return variants.en
          }

          // Fallback to source language if specified
          if (item.sourceLocale && variants[item.sourceLocale]) {
            return variants[item.sourceLocale]
          }
        }

        // Fallback to original notification content
        return item.notification || ''
      },
    },
    created() {
      // Suppress ResizeObserver errors that are common with Vuetify tables and language switching
      const originalError = window.onerror
      window.onerror = (message, source, lineno, colno, error) => {
        if (
          message &&
          message.includes(
            'ResizeObserver loop completed with undelivered notifications'
          )
        ) {
          return true // Suppress this specific error
        }
        if (originalError) {
          return originalError(message, source, lineno, colno, error)
        }
        return false
      }

      if (localStorage.setNotificationsHeaders) {
        const newDefaults = JSON.parse(
          localStorage.getItem('setNotificationsHeaders')
        )

        // Use map to get a simple array of values. Ex: [1,4]
        let defaultFilter = newDefaults.map((itemY) => itemY.key)

        // Use filter and includes to filter the full dataset by the filter dataset's value.
        let filtered = this.headers.filter((item) =>
          defaultFilter.includes(item.key)
        )

        this.selectedHeaders = filtered
      } else {
        this.selectedHeaders = this.headers.filter((s) => s.default)
      }

      this.getNotifications()
    },
  }
</script>

<style scoped>
  .send-btn {
    background-color: #a5d867;
    color: black;
    font-weight: bold;
    border-radius: 8px;
    text-transform: uppercase;
  }

  .send-btn:disabled {
    background-color: #e0e0e0 !important;
    color: #9e9e9e !important;
    cursor: not-allowed;
  }
  .notifications-table {
    margin-bottom: 25%;
  }
  .notifications-table .notification-wrapper p {
    margin-bottom: 0;
  }
  .notifications-table .expanded-editor:not(.dark) {
    background-color: #fff;
  }
  #notifications .sml-editor.dark .counter {
    background: #1a1a1a !important;
  }
  .type-select {
    min-width: 100px;
  }
  .audience-select {
    min-width: 150px;
  }
  ::v-deep(.v-row > .v-checkbox) {
    min-width: 100px; /* or 200px based on label length */
    flex-grow: 0;
    flex-shrink: 0;
  }

  .disabled-icon {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }

  .read-only-warning {
    margin-top: 8px;
  }
</style>
